import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Alert,
  Badge,
  type BadgeProps,
  Box,
  Button,
  ButtonDropdown,
  type ButtonDropdownProps,
  Container,
  CopyToClipboard,
  ExpandableSection,
  Header,
  Icon,
  KeyValuePairs,
  type KeyValuePairsProps,
  Link as CloudscapeLink,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
} from '@cloudscape-design/components';
import type { RuleDetails, RuleInput, RuleSummary } from '@amzn/helianthus-arbiter-client';
import { CodeView } from '@cloudscape-design/code-view';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { RuleStatusBadge, ruleStatusLabel } from '../../components/RuleStatusBadge';
import { ArnDisplay } from '../../components/ArnDisplay';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useRule } from '../../hooks/useRule';
import { useRuleProvenance } from '../../hooks/useRuleProvenance';
import { useRuleVersions } from '../../hooks/useRuleVersions';
import { useUrlSearchState } from '../../hooks/useUrlSearchState';
import { DependencyGraph } from '../../components/DependencyGraph';
import { LoadingState } from '../../components/LoadingState';
import { infoPopover } from '../../components/InfoPopover';
import { ProvenanceAlert } from '../../components/ProvenanceAlert';
import { formatDateTime, humanizeFieldName } from '../../utils/formatFieldName';
import { buildRuleUrl } from '../../utils/evaluationHelpers';
import { registryRuleUrl } from '../../utils/arnUtils';
import { SOURCE_TYPES, sourceTypeOf } from './ruleInputs';
import { SERVICE_PROPERTIES, SERVICE_PROPERTY_KEYS } from './serviceProperties';
import { RuleLoadError } from './RuleLoadError';
import { RecentEvaluationsTab } from './RecentEvaluationsTab';

/** Registry rules carry CWEs as one string, e.g. "CWE-835,CWE-754". Each becomes a link to its MITRE entry. */
function cweLinks(value: string): React.ReactNode {
  const ids = value.split(',').map((id) => id.trim());
  if (!ids.every((id) => /^CWE-\d+$/i.test(id))) return value;
  return (
    <SpaceBetween direction="horizontal" size="xs">
      {ids.map((id) => (
        <CloudscapeLink
          key={id}
          href={`https://cwe.mitre.org/data/definitions/${id.replace(/\D/g, '')}.html`}
          external
          externalIconAriaLabel="Opens in a new tab"
        >
          {id}
        </CloudscapeLink>
      ))}
    </SpaceBetween>
  );
}

function formatMetadataValue(key: string, value: string): React.ReactNode {
  if (key === 'creation_time' && /^\d{9,13}$/.test(value)) {
    return formatDateTime(value.length <= 10 ? Number(value) * 1000 : Number(value));
  }
  if (/^cwe/i.test(key)) return cweLinks(value);
  if (/^https?:\/\/\S+$/.test(value)) {
    return (
      <CloudscapeLink href={value} external externalIconAriaLabel="Opens in a new tab">
        {value}
      </CloudscapeLink>
    );
  }
  return value;
}

const VERSION_INFO = infoPopover(
  'Version',
  'Every accepted update creates a new version. This page shows the latest version unless one is chosen from the Version menu; older versions are read-only.',
);

const SCOPE_INFO = infoPopover(
  'Scope',
  'The security concern this rule covers, and the service characteristics a service must have for it to apply. '
  + 'A property with no value places no constraint: a rule with none set is generic and applies to every service.',
);

export function RuleDetailPage() {
  const { ruleName } = useParams<{ ruleName: string }>();
  const urlState = useUrlSearchState();
  const requestedVersion = urlState.get('version') || undefined;
  const { rule, parents, children, loading, error } = useRule(ruleName, { version: requestedVersion, includeDependencies: true });
  useDocumentTitle(rule ? `Rule: ${rule.ruleName}` : 'Rule');

  // Keep the current rule on screen while a different version loads or fails; only the first load blanks the page.
  if (loading && !rule) return <LoadingState label="Loading rule..." />;
  if (!rule) return <RuleLoadError ruleName={ruleName} error={error} />;
  return <RuleDetail rule={rule} parents={parents} children={children} requestedVersion={requestedVersion} loadError={error} />;
}

interface RuleDetailProps {
  rule: RuleDetails;
  parents: RuleSummary[];
  children: RuleSummary[];
  requestedVersion: string | undefined;
  /** A refetch (version switch) that failed while `rule` stayed on screen. */
  loadError: Error | null;
}

function RuleDetail({ rule, parents, children, requestedVersion, loadError }: RuleDetailProps) {
  const ruleName = rule.ruleName!;
  const ruleUrl = buildRuleUrl(ruleName);
  const navigate = useNavigate();
  const client = useArbiterClient();
  // The tab default is declared so `set` deletes the param for it, leaving ?tab out of the URL for
  // the default tab and ?version untouched either way.
  const urlState = useUrlSearchState({ tab: 'details' });
  const { versions, latestVersion, loading: versionsLoading, error: versionsError } = useRuleVersions(ruleName);
  const { principal, principalClass } = useRuleProvenance(rule);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // DescribeRule rewrites @CURRENT to a number, so "latest" only comes from the versions list. While it loads
  // the page reads as current (Edit always opens the latest anyway). If the list fails, an unknown latest reads
  // as historical on purpose: the alternative is offering Edit on a page that may be showing an old version.
  // The banner then says "Latest is version unknown" and its View latest link drops the param to recover.
  // Read-only follows the version on screen, not the one in the URL: a switch that is still loading or
  // that failed leaves the previous rule showing.
  const isHistorical = !versionsLoading
    && !!requestedVersion && requestedVersion !== '@CURRENT' && rule.canonicalVersion !== latestVersion;

  const versionItems: ButtonDropdownProps.Items = versionsError
    ? [{ id: 'error', text: 'Could not load versions', disabled: true }]
    : versions.map((v) => {
      const number = v.canonicalVersion ?? '';
      return {
        id: number,
        text: `Version ${number}`,
        labelTag: number === latestVersion ? 'Current' : undefined,
        // A stored status on an old row describes that version's past; only the current row's is live.
        secondaryText: [
          number === latestVersion ? ruleStatusLabel(v.ruleStatus) : 'Superseded',
          v.severity,
          formatDateTime(v.versionCreatedAt ?? v.lastModifiedAt),
        ].filter(Boolean).join(' · '),
        disabled: number === rule.canonicalVersion,
      };
    });

  // The version gate sits on the Actions trigger; only the parents guard belongs to an individual item.
  const actionItems: ButtonDropdownProps.Items = [
    { id: 'edit', text: 'Edit', iconName: 'edit' },
    {
      id: 'delete',
      text: 'Delete',
      iconName: 'remove',
      disabled: parents.length > 0,
      disabledReason: `Used by ${parents.map((p) => p.ruleName).join(', ')}`,
    },
  ];

  const closeDeleteModal = () => {
    setDeleteVisible(false);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await client.deleteRule({ ruleName });
      navigate('/rules');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const sourceRows: KeyValuePairsProps.Pair[] = principalClass === 'registry'
    ? [{
      label: 'Source',
      value: (
        <CloudscapeLink href={registryRuleUrl(ruleName)} external externalIconAriaLabel="Opens in a new tab">
          Rules registry
        </CloudscapeLink>
      ),
    }]
    : principalClass === 'automation'
      ? [{ label: 'Source', value: <ArnDisplay value={principal} /> }]
      : [];

  const badgeRow = (values: string[]): React.ReactNode => (
    <SpaceBetween direction="horizontal" size="xs">
      {values.map((value) => (
        <Badge key={value} color="blue">{value}</Badge>
      ))}
    </SpaceBetween>
  );

  // Set properties are listed; unset ones are named in one line, so all 17 stay visible without 17 empty rows.
  const constrained: KeyValuePairsProps.Pair[] = [];
  const unconstrained: string[] = [];
  for (const key of SERVICE_PROPERTY_KEYS) {
    const values = rule.serviceProperties?.[key] ?? [];
    if (values.length) constrained.push({ label: SERVICE_PROPERTIES[key].label, value: badgeRow(values) });
    else unconstrained.push(SERVICE_PROPERTIES[key].label);
  }
  const scopeRows: KeyValuePairsProps.Pair[] = [
    {
      label: 'Security concern',
      value: rule.securityConcern && rule.securityConcern !== 'Unspecified'
        ? <Badge>{rule.securityConcern}</Badge>
        : <Box color="text-body-secondary">Unspecified</Box>,
    },
    ...constrained,
  ];

  const tagRows: KeyValuePairsProps.Pair[] = Object.entries(rule.tags ?? {}).map(([key, value]) => ({ label: key, value }));

  const metadataEntries = Object.entries(rule.metadata ?? {});
  const inputs = rule.inputs ?? [];
  const dependencyCount = parents.length + children.length;
  const expression = rule.evaluation?.expression ?? '';

  const renderSourceType = (input: RuleInput): React.ReactNode => {
    const type = sourceTypeOf(input.source);
    if (!type) return 'Unknown';
    return (
      <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
        <Icon name={SOURCE_TYPES[type].icon} />
        <span>{SOURCE_TYPES[type].label}</span>
      </SpaceBetween>
    );
  };

  const renderSourceDetail = (input: RuleInput): React.ReactNode => {
    const source = input.source;
    if (!source) return '';
    if (source.discovery) {
      return `type: ${source.discovery.discoveryType}${source.discovery.filter ? `, filter: ${source.discovery.filter.expression}` : ''}`;
    }
    const key = source.arbiter?.key ?? source.call?.key;
    if (key) return `key: ${key}`;
    if (source.rule?.target) {
      const target = source.rule.target;
      const href = buildRuleUrl(target);
      return (
        <CloudscapeLink href={href} onFollow={(e) => { e.preventDefault(); navigate(href); }}>
          {target}
        </CloudscapeLink>
      );
    }
    return '';
  };

  const details = (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            // Both controls sit on this tab rather than the page header because both act on
            // the single version shown here, and recent evaluations span every version of
            // the rule, so neither has anything to act on over there.
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  ariaLabel="Select rule version"
                  iconName="history"
                  items={versionItems}
                  loading={versionsLoading}
                  loadingText="Loading versions"
                  expandToViewport
                  onItemClick={({ detail }) => urlState.set('version', detail.id === latestVersion ? '' : detail.id)}
                >
                  Version {rule.canonicalVersion}
                </ButtonDropdown>
                {rule.ruleStatus !== 'deleted' && (
                  <ButtonDropdown
                    items={actionItems}
                    disabled={isHistorical}
                    disabledReason="Switch to the latest version to edit or delete"
                    expandToViewport
                    onItemClick={({ detail }) => (detail.id === 'edit' ? navigate(`${ruleUrl}/edit`) : setDeleteVisible(true))}
                  >
                    Actions
                  </ButtonDropdown>
                )}
              </SpaceBetween>
            }
          >
            Details
          </Header>
        }
      >
        <KeyValuePairs
          columns={3}
          items={[
            {
              type: 'group',
              title: 'General',
              items: [
                { label: 'Description', value: rule.description || '-' },
                {
                  label: 'Version',
                  value: (
                    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                      <span>{rule.canonicalVersion}</span>
                      {!isHistorical && <Badge color="green">Current</Badge>}
                    </SpaceBetween>
                  ),
                  info: VERSION_INFO,
                },
                {
                  label: 'Status',
                  value: isHistorical
                    ? <StatusIndicator type="stopped">Superseded (was {ruleStatusLabel(rule.ruleStatus)})</StatusIndicator>
                    : <RuleStatusBadge status={rule.ruleStatus} />,
                },
                {
                  label: 'Severity',
                  value: rule.severity
                    ? <Badge color={`severity-${rule.severity.toLowerCase()}` as BadgeProps['color']}>{rule.severity}</Badge>
                    : '-',
                },
                ...(rule.stateReason ? [{ label: 'State Reason', value: rule.stateReason }] : []),
              ],
            },
            {
              type: 'group',
              title: 'Targeting',
              items: [
                { label: 'Applies To', value: rule.appliesTo?.length ? badgeRow(rule.appliesTo) : '--' },
                ...tagRows,
              ],
            },
            {
              type: 'group',
              title: 'Provenance',
              items: [
                { label: 'Created By', value: <ArnDisplay value={rule.createdBy} /> },
                { label: 'Created At', value: formatDateTime(rule.createdAt) },
                { label: 'Last Modified By', value: <ArnDisplay value={rule.lastModifiedBy} /> },
                { label: 'Last Modified At', value: formatDateTime(rule.lastModifiedAt) },
                ...sourceRows,
              ],
            },
          ]}
        />
        {metadataEntries.length > 0 && (
          <Box margin={{ top: 'l' }}>
            <Header variant="h3">Metadata</Header>
            <KeyValuePairs
              items={metadataEntries.map(([key, value]) => ({
                label: humanizeFieldName(key),
                value: formatMetadataValue(key, value),
              }))}
            />
          </Box>
        )}
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            info={SCOPE_INFO}
            description="The security concern this rule covers and the service characteristics it requires"
          >
            Scope
          </Header>
        }
      >
        <SpaceBetween size="m">
          <KeyValuePairs columns={3} items={scopeRows} />
          <Box color="text-body-secondary" fontSize="body-s">
            {unconstrained.length === SERVICE_PROPERTY_KEYS.length
              ? 'No service property constraints: this rule applies to every service.'
              : `Unconstrained (any value applies): ${unconstrained.join(', ')}.`}
          </Box>
        </SpaceBetween>
      </Container>

      <Table
        header={<Header variant="h2" counter={`(${inputs.length})`}>Inputs</Header>}
        columnDefinitions={[
          { id: 'name', header: 'Name', cell: (item: RuleInput) => item.name },
          { id: 'type', header: 'Type', cell: (item: RuleInput) => item.type },
          { id: 'sourceType', header: 'Source type', cell: renderSourceType },
          { id: 'sourceDetail', header: 'Source detail', cell: renderSourceDetail },
        ]}
        items={inputs}
        wrapLines
        empty={<Box textAlign="center">No inputs defined</Box>}
      />

      <ExpandableSection
        variant="container"
        headerText="Evaluation"
        headerActions={<Badge color="blue">{rule.evaluation?.language ?? 'sangheli-1.0'}</Badge>}
        defaultExpanded
      >
        <CodeView
          content={expression}
          lineNumbers
          wrapLines
          ariaLabel="Rule expression"
          actions={
            <CopyToClipboard
              variant="icon"
              textToCopy={expression}
              copySuccessText="Expression copied"
              copyErrorText="Failed to copy"
              copyButtonAriaLabel="Copy expression"
            />
          }
        />
      </ExpandableSection>

      <ExpandableSection
        variant="container"
        headerText="Dependencies"
        headerCounter={`(${dependencyCount})`}
        defaultExpanded={dependencyCount > 0}
      >
        <DependencyGraph ruleName={ruleName} severity={rule.severity} parents={parents} children={children} />
      </ExpandableSection>
    </SpaceBetween>
  );

  const tabs = [
    { id: 'details', label: 'Details', content: details },
    { id: 'recent', label: 'Recent evaluations', content: <RecentEvaluationsTab ruleName={ruleName} /> },
  ];
  // Checked against the tabs above rather than forwarded: a stale or typo'd ?tab would otherwise
  // select no tab at all and leave the page blank.
  const requestedTab = urlState.get('tab');
  const activeTabId = tabs.some((tab) => tab.id === requestedTab) ? requestedTab : tabs[0].id;

  return (
    <SpaceBetween size="l">
      <Header variant="h1">{rule.ruleName}</Header>

      {loadError && (
        <Alert
          type="error"
          header={`Could not load ${requestedVersion ? `version ${requestedVersion}` : 'the latest version'}`}
          // Re-selecting the same version would not refetch: the URL never changes. Offer the way out instead.
          buttonText={requestedVersion ? 'View latest' : 'Reload'}
          onButtonClick={() => (requestedVersion ? urlState.remove('version') : window.location.reload())}
        >
          {loadError.message}
        </Alert>
      )}

      <ProvenanceAlert rule={rule} />

      {isHistorical && (
        <Alert type="info">
          Viewing version {rule.canonicalVersion}. Latest is version {latestVersion ?? 'unknown'}.{' '}
          <CloudscapeLink href={ruleUrl} onFollow={(e) => { e.preventDefault(); urlState.remove('version'); }}>
            View latest
          </CloudscapeLink>
        </Alert>
      )}

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => urlState.set('tab', detail.activeTabId)}
        tabs={tabs}
      />

      <Modal
        visible={deleteVisible}
        onDismiss={closeDeleteModal}
        header="Delete rule"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={closeDeleteModal}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete} loading={deleting}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {deleteError && <Alert type="error">{deleteError}</Alert>}
          <span>
            Delete rule <strong>{rule.ruleName}</strong>? This marks the rule as deleted. It stops being evaluated but
            remains viewable, including its version history.
          </span>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
